import { nonnull } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";
import { CalendarReadRevision } from "@tento-lona/sheets-ui/sheet/calendar-read-revision.ts";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import {
  type BindCellContext,
  type ContentProviders,
  type FixedChildRowContext,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type Option } from "@tento-lona/sheets";
import { LiveData } from "@tento-core/livedata";
import { DateFragment, TimezoneRegion, Tzabbr } from "@tento-chrono";
import type { FixedChildSpec } from "@tento-ui/components/sheet/fixed-child-culling";
import { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { RowId } from "@tento-lona/sheets";
import type { EventItemPresentation, FeatureFlag } from "@tento-lona";
import { DarkMode } from "@tento-ui/dark-mode";
import { FrameMarks } from "@tento-ui/frame-marks";
import { ThemesClient } from "@tento-lona";
import { FlightInfo } from "@tento-lona";
import type { GestureCoordinatorLike } from "@tento-lona/sheets-ui/sheet/gestures/gesture-system.ts";
import { DragGhostService } from "@tento-lona/sheets-ui/sheet/gestures/drag-ghost-service.ts";
import { CalendarSheetColumn } from "@tento-lona/sheets-ui/plugins/layout/timeline-column/components/calendar-sheet-column.ts";
import { PIXELS_PER_HOUR } from "@tento-ui/components/calendar/calendar-hour";
import { writeColumnSkeletonSpan } from "./components/calendar-column-skeleton.ts";
import { SheetViewModel } from "@tento-lona";
import { collectCalendarSubcolumns } from "@tento-lona/sheets-ui/plugins/shared/calendar-subcolumn/provider-discovery.ts";
import { CalendarPreferences } from "@tento-lona";
import { type SheetRow } from "@tento-lona";
import { type TimedGridConfig } from "@tento-lona/sheets";
import { CalendarBodyRow, TimelineColumnRow, AliasRow, LayoutRow } from "@tento-lona/sheets/platform";
import { CalendarColumnGestures } from "./calendar-column-gestures.ts";
import { StripWorkQueue, stripBudgetEnabled } from "./components/calendar-strip-work.ts";
import { ContentLagMeter } from "./components/calendar-content-lag.ts";
import { CurrentTimeClock } from "./current-time-clock.ts";
import { liveVariantOf } from "../../../sheet/live-variant.ts";
import type { SheetBinder } from "../../../sheet/sheet-index-renderer.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";
import type { PageLifecycle } from "@tento-lona/host";

/** Narrow bridge for the workspace-only timeline-column harness door. */
/** `DayColumnRenderer`'s friend record (`Internals`): its constructor registers each instance. */
type DayColumnRendererInternals = {
  readonly bindWithLoadGates: (
    renderer: DayColumnRenderer,
    column: CalendarSheetColumn,
    context: BindCellContext,
    config: TimedGridConfig,
    provider: () => Promise<void>,
    cells: () => Promise<void>,
  ) => Promise<void>;
};
const dayColumnRendererInternals = new Internals<DayColumnRenderer, DayColumnRendererInternals>("DayColumnRenderer");

export function bindTimelineColumnWithLoadGatesForHarness(
  renderer: DayColumnRenderer,
  column: CalendarSheetColumn,
  context: BindCellContext,
  config: TimedGridConfig,
  provider: () => Promise<void>,
  cells: () => Promise<void>,
): Promise<void> {
  return dayColumnRendererInternals.of(renderer).bindWithLoadGates(renderer, column, context, config, provider, cells);
}

/**
 * Resolve the timed-grid config from the bound layout row's OWN variant — the
 * `calendar-body` body (which itself falls back to its parent alias for legacy
 * config) or a standalone `timeline[column]`. Replaces the old
 * `row.findAncestor(CalendarAliasRow)` upward read so this generic timed-grid
 * layout never depends on the calendar alias. Also fixes forked calendars:
 * a hydrated/forked body that can't resolve its alias ancestor still renders
 * (its own config), instead of hiding. `null` only when the bound row is
 * neither a body nor a `timeline[column]` (the caller then hides / no-ops).
 */
export function resolveTimedGridConfig(
  row: SheetRow.Joined.Handle,
): Option<TimedGridConfig> {
  const variant = liveVariantOf(row);
  if (
    variant instanceof CalendarBodyRow || variant instanceof TimelineColumnRow
  ) {
    return variant.timedConfig();
  }
  const body = row.findAncestor(CalendarBodyRow) ??
    row.findAncestor(TimelineColumnRow);
  return body?.timedConfig() ?? null;
}

export function visibleTimelineColumnAnchorRow(
  row: SheetRow.Joined.Handle,
): SheetRow.Joined.Handle {
  const parent = row.parent();
  const variant = liveVariantOf(row);
  const isColumnLayoutRow = row.descriptor.id === "timeline[column]" ||
    (variant instanceof LayoutRow &&
      variant.descriptor.layout === "timeline[column]");
  const parentVariant = parent == null ? null : liveVariantOf(parent);
  if (
    isColumnLayoutRow &&
    parent &&
    ((parentVariant instanceof AliasRow &&
      (parentVariant.descriptor.template === "calendar" ||
        parentVariant.descriptor.template === "alias.calendar")) ||
      parent.descriptor.id === "calendar")
  ) {
    return parent;
  }
  return row;
}

const DEBUG_COLOR_MAP: Map<Tzabbr, string> = new Map();
let colorIndex = 0;
const COLORS = [
  "var(--red-dimmed)",
  "var(--orange-dimmed)",
  "var(--yellow-dimmed)",
  "var(--green-dimmed)",
  "var(--blue-dimmed)",
  "var(--teal-dimmed)",
  "var(--light-purple-dimmed)",
];

/**
 * Renders one timed day column — the `CalendarSheetColumn` make/bind, its
 * subcolumn discovery, and the interaction state it binds against (the
 * `gestureManager`, the legend↔body `chromeBus`, and the per-tz background
 * theming). It is a pure renderer: it paints whatever timed config it is
 * handed and owns no clock, legend, or invalidation wiring — those are the
 * layout plugin's. The plugin composes one of these.
 */
export class DayColumnRenderer {
  static readonly #internals: DayColumnRendererInternals = {
    bindWithLoadGates: (renderer, column, context, config, provider, cells) =>
      renderer.#bind(column, context, config, {provider, cells}),
  };

  static readonly MARGIN_TOP = 12;
  static readonly MARGIN_BOTTOM = 12;
  static readonly TAIL_BOTTOM = 20;

  /**
   * The gesture + cursor system — the per-column `CalendarGesture.Manager` and
   * the legend↔body `CalendarChromeBus` it drives. The layout plugin and
   * plugin-manager reach `gestures.manager` through this renderer to register
   * gestures; the plugin's legend reads `gestures.chromeBus`.
   */
  readonly gestures = new CalendarColumnGestures();

  readonly columnDeps: CalendarSheetColumn.Dependencies;
  readonly columnSignals: CalendarSheetColumn.Signals;

  readonly #deps: DayColumnRenderer.Deps;
  readonly #stripWork: Option<StripWorkQueue>;
  /** The visible days `[start, end)` (days since the epoch) the layout last reported. */
  #visibleDays: Option<{ start: number; end: number }> = null;
  /** How far this renderer's strips' content trails the visible days (`scroll/content-lag`). */
  readonly #contentLag = ContentLagMeter.forDocument();

  /** Release the work queue when its layout plugin retires. */
  dispose(): void {
    this.#stripWork?.dispose();
  }

  constructor(deps: DayColumnRenderer.Deps) {
    dayColumnRendererInternals.register(this, DayColumnRenderer.#internals);
    this.#deps = deps;
    this.#stripWork = stripBudgetEnabled()
      ? StripWorkQueue.forDocument(() => this.#visibleDays)
      : null;
    this.columnDeps = {
      stripWork: this.#stripWork,
      gestureManager: this.gestures.manager,
      theme: {
        columnBackgroundColor: () => {
          return "var(--divider)";
        },
        backgroundColor: {
          // tento-ui-js's colour callback passes its ambient `Option`.
          backgroundColor: (df: DateFragment | null | undefined) => {
            if (!this.#deps.flags.eventsPluginTzBg.get() || !df) {
              const t = this.#deps.themes.live.get() as Option<ThemesClient.Theme>;
              return (
                t?.semanticColor(
                  "sheet",
                  "background",
                  DarkMode.isDarkMode() ? "dark" : "light",
                ) ?? "var(--bg)"
              );
            }

            const tzabbr = df.tz.info.tzabbr;
            if (!tzabbr) return "var(--bg)";

            let color = DEBUG_COLOR_MAP.get(tzabbr);
            if (color) return color;

            color = nonnull(COLORS[colorIndex++ % COLORS.length], "the debug palette is non-empty");
            DEBUG_COLOR_MAP.set(tzabbr, color);
            return color;
          },
        },
      },
      tooltip: null,
      presentation: this.#deps.presentation,
      contentLag: this.#contentLag,
    };
    this.columnSignals = {
      currentTime: this.#deps.clock.time,
      currentGestureCount: this.gestures.gestureCount,
      addPendingEventRenderer: (renderer) => {
        return this.#deps.ghosts.addRenderer(renderer);
      },
    };
  }

  /**
   * Calendar content provider for the timed grid. The full-day aggregation
   * method retired 2026-05-07 — all-day events flow through
   * `TimelineRowEventRendererPlugin.resolveContentProvider` →
   * `findTimelineProviderUnderCalendarRow` directly.
   */
  #calendarProvider: CalendarSubcolumn.Provider = {
    subcolumnFromRow(
      _sheet: SheetViewModel.Surface<SheetBinder>,
      _row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      return {
        rowId: RowId.fromLocalId(_row.id),
        width: { type: "natural" },
        makeLayout: () => CalendarColumnLayoutGroup.make(),
        // No in-grid spans: both members answer none (why the sync one too:
        // `CalendarSubcolumn.makeSpansSync`).
        makeSpansSync: () => [],
        makeSpans: async () => [],
      };
    },
  };

  readonly contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };

  /** The layout plugin's viewport hook threads the sheet tooltip in once it
   *  exists (it is not available at construction). */
  ensureTooltip(tooltip: CalendarSheetColumn.Dependencies["tooltip"]): void {
    if (this.columnDeps.tooltip == null) this.columnDeps.tooltip = tooltip;
  }

  /** The layout plugin's viewport hook reports the visible days (days since
   *  epoch, end exclusive); strip work runs the strips nearest them first. */
  noteVisibleDays(days: { start: number; end: number }): void {
    this.#visibleDays = days;
    this.#contentLag.noteVisibleDays(days);
  }

  makeCell(): CalendarSheetColumn {
    const $column = CalendarSheetColumn.make();
    $column.init(this.columnDeps, this.columnSignals);
    $column.assignStyles({
      height: "100%",
      marginTop: DayColumnRenderer.MARGIN_TOP + "px",
      marginBottom: DayColumnRenderer.MARGIN_BOTTOM + "px",
      "--column-tail-bottom": DayColumnRenderer.TAIL_BOTTOM + "px",
    });

    this.columnSignals.currentTime.addListener(
      $column,
      (t) => {
        $column.style.setProperty("--current-time-mse", String(t.mse));
      },
      LiveData.TriggerImmediately,
    );

    this.#deps.coordinator.registerClickOutside($column);
    return $column;
  }

  getSubcolumns(
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
    pluginManager: SheetDelegate.PluginManager,
  ): CalendarSubcolumn[] {
    return collectCalendarSubcolumns(sheet, row, pluginManager);
  }

  /**
   * The timed-grid surface for one day column — sets the column visible and
   * binds `CalendarSheetColumn` with the day's window + subcolumns. The caller
   * (the layout plugin) decides whether to paint at all (list / month hide) and
   * passes the resolved `TimedGridConfig`.
   */
  async bind(
    $column: CalendarSheetColumn,
    context: BindCellContext,
    config: TimedGridConfig,
  ): Promise<void> {
    return this.#bind($column, context, config, null);
  }

  async #bind(
    $column: CalendarSheetColumn,
    { sheet, sheetRow, column, handle }: BindCellContext,
    config: TimedGridConfig,
    testLoads: Option<{ provider: () => Promise<void>; cells: () => Promise<void> }>,
  ): Promise<void> {
    // Own the request before the first await, including timezone resolution.
    const stillValid = $column.beginDataLoad();
    const waitStart = FrameMarks.stamp();
    try {
      const timezone = config.timezone;
      const hoursRange = config.hoursRange();
      $column.style.display = "";

      // Hide stale content synchronously before any async work.
      const date = column.discrete();
      if (!$column.propsEq(date)) {
        $column.toggleLoadingState(true, [date[0], date[1]]);
      }
      const partialWindow = {
        start: column.partialLocalTop ?? null,
        end: column.partialLocalBottom ?? null,
      };
      // The skeleton paints only the hours events can render in (a day with a
      // flight) from this frame on: a reused column must not keep its last
      // day's span until the loads below settle.
      writeColumnSkeletonSpan($column, {
        date: date[0].start,
        zone: date[1],
        window: hoursRange,
        partialWindow,
        outside: this.columnDeps.theme.columnBackgroundColor(),
        pixelsPerHour: PIXELS_PER_HOUR,
      });

      const lonaSheet = sheet;
      if (!lonaSheet.isTimezoneLocked && timezone) {
        date[1] = await TimezoneRegion.get(timezone);
        if (!stillValid()) return;
      }

      const prev: ColumnIndex.Key.Resolved<FlightInfo> =
        sheet.indexRenderer.mapper.resolveKey(
          ColumnIndex.Key.fromIndex(column.offset - 1),
        );
      const prevFlight = prev.castToCustom();

      const window = hoursRange;
      const anchorRow = visibleTimelineColumnAnchorRow(sheetRow);
      const props: CalendarSheetColumn.Props = {
        readRevision: CalendarReadRevision.for(sheet.invalidation),
        readEnvironment: () => [
          this.#deps.flags.eventsPluginTzBg.get(), DarkMode.isDarkMode(),
          this.#deps.themes.live.get(),
        ],
        date,
        window,
        partialWindow,
        rowId: anchorRow.id,
        row: sheetRow,
        subcolumns: testLoads ? [{
          ...this.#calendarProvider.subcolumnFromRow(sheet, sheetRow, sheetRow, handle.pluginManager),
          loadData: testLoads.provider,
        }] : this.getSubcolumns(sheet, sheetRow, handle.pluginManager),
        hideWeekDivider: prevFlight !== null,
        column,
        startOfWeek: this.#deps.calendarPreferences.startOfWeek(),
      };

      if (!$column.propsEq(date)) {
        $column.toggleLoadingState(true, props.date);
      }

      const loadPromises = props.subcolumns
        .flatMap((s) => s.loadData == null ? [] : [s.loadData(date)]);
      // Provider loads cover the rows-side install. The parallel sheet-cell
      // response may still be waiting on the ingestion gate; await both before
      // capturing a calendar read of the installed state.
      await Promise.all([
        ...loadPromises,
        testLoads ? testLoads.cells() : sheet.cells.ensureLoaded(date[0].toRange()),
      ]);
      if (!stillValid()) return;
      FrameMarks.latency("day-column.wait", waitStart, props.subcolumns.length, 0, column.offset);
      const bindStart = FrameMarks.begin();
      $column.bind(props);
      FrameMarks.end("day-column.bind", bindStart, props.subcolumns.length, 0, column.offset);
    } catch (error) {
      if (stillValid()) {
        $column.endLoadingPreview();
        // A load the browser aborted because this page is unloading is
        // recorded at info, not reported (`PageLifecycle`).
        this.#deps.page.reportUnlessLeaving(error instanceof Error ? error : new Error(String(error)), () =>
          Tracing.err("ui")("calendar data load failed", {
            rowId: String(sheetRow.id), error: String(error),
          }));
      }
    }
  }

  /**
   * Sheet-level overlay specs (e.g. sticker cutouts) from the timed subcolumns'
   * opt-in `makeFixedChildren` surface. The caller gates list / month; this
   * paints the timed overlays.
   */
  async fixedChildren(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
    config: TimedGridConfig,
  ): Promise<FixedChildSpec[]> {
    const sheet = context.sheet;
    const subcolumns = this.getSubcolumns(
      sheet,
      row,
      context.handle.pluginManager,
    );
    const fixedChildrenContext: CalendarSubcolumn.FixedChildrenContext = {
      renderedColumns: context.renderedColumns,
      validHours: config.hoursRange(),
      refresh: () => context.handle.rebind.rebindViewports(),
      handle: context.handle,
      // The actual band these fixed children register under (may differ
      // from this plugin's render `row`).
      bandRowId: context.bandRowId,
    };
    const specs = await Promise.all(
      subcolumns.map(
        (subcolumn) =>
          subcolumn.makeFixedChildren?.(fixedChildrenContext) ??
          Promise.resolve([]),
      ),
    );
    return specs.flat();
  }

  // The column fills its cell through normal layout. No descendants consume a
  // --column-width variable; publishing one restyles every timed event.
  onResize(_$cell: CalendarSheetColumn, _context: BindCellContext): void {}
}

/** Types that go with `DayColumnRenderer`: `Deps`. */
export namespace DayColumnRenderer {
  export type Deps = {
    /** The shared current-time clock (feeds `--current-time-mse` + signals). */
    clock: CurrentTimeClock;
    /** Drag-ghost service the days-rows bind their repaint callbacks to. */
    ghosts: DragGhostService;
    coordinator: GestureCoordinatorLike;
    themes: ThemesClient;
    calendarPreferences: CalendarPreferences;
    flags: {
      eventsPluginTzBg: FeatureFlag;
    };
    /** The client's event categories and drafts (`sdk.events`), for the day bodies' event paint. */
    presentation: EventItemPresentation.Scope;
    /** The page's lifecycle (`platformOf(client).page`): a load the page's
     *  unload aborted is recorded at info, not reported. */
    page: PageLifecycle;
  };
}
